import { Button } from '@heidi/design-system/button';
import {
  TypographyCaption,
  TypographyCaptionBold,
} from '@heidi/design-system/typography';
import { PersonalizedEvidenceSuggestionCategory } from '@heidi/schema/ml-scribe';
import { cn } from '@heidi/utils';
import type { LucideIcon } from 'lucide-react';
import { BookOpen, FileText, Lightbulb, ListChecks, Sparkles, X } from 'lucide-react';
import type { ReactElement } from 'react';

const PERSONALIZED_EVIDENCE_CATEGORY_ICONS: Record<
  PersonalizedEvidenceSuggestionCategory,
  LucideIcon
> = {
  [PersonalizedEvidenceSuggestionCategory.research]: BookOpen,
  [PersonalizedEvidenceSuggestionCategory.planning]: ListChecks,
  [PersonalizedEvidenceSuggestionCategory.documentation]: FileText,
  [PersonalizedEvidenceSuggestionCategory.reflection]: Lightbulb,
};

function getPersonalizedEvidenceCategoryIcon(
  category: PersonalizedEvidenceSuggestionCategory | null | undefined
): LucideIcon {
  if (category == null) return Sparkles;
  return PERSONALIZED_EVIDENCE_CATEGORY_ICONS[category] ?? Sparkles;
}

export type SessionlessEvidenceAwarenessPopoverCardProps = {
  title: string;
  suggestion: string;
  ctaButtonLabel: string;
  category: PersonalizedEvidenceSuggestionCategory | null | undefined;
  onDismiss: () => void;
  onCta: () => void;
  className?: string;
};

export function SessionlessEvidenceAwarenessPopoverCard({
  title,
  suggestion,
  ctaButtonLabel,
  category,
  onDismiss,
  onCta,
  className,
}: SessionlessEvidenceAwarenessPopoverCardProps): ReactElement {
  const EvidenceCategoryIcon = getPersonalizedEvidenceCategoryIcon(category);

  return (
    <div
      className={cn(
        'flex w-full flex-col gap-2 overflow-hidden rounded-lg border border-border py-3',
        'bg-surface-container text-text-primary',
        className
      )}
    >
      <div className="flex flex-col gap-1">
        <div className="flex items-start gap-2 px-3 py-0.5">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <EvidenceCategoryIcon
              className="size-4 shrink-0 text-text-primary"
              aria-hidden
            />
            <TypographyCaptionBold className={cn('min-w-0 flex-1 text-text-primary')}>
              {title}
            </TypographyCaptionBold>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="xxs"
            iconOnly
            flush="all"
            className="shrink-0 text-text-primary"
            aria-label="Close"
            onClick={onDismiss}
          >
            <X />
          </Button>
        </div>
        <div className="px-3 py-0">
          <TypographyCaption className={cn('text-text-secondary', 'text-wrap')}>
            {suggestion}
          </TypographyCaption>
        </div>
      </div>
      <div className="flex flex-col px-2 py-0">
        <Button
          type="button"
          variant="secondary"
          size="xs"
          className="w-full shrink-0"
          onClick={onCta}
        >
          {ctaButtonLabel}
        </Button>
      </div>
    </div>
  );
}
